import { useState, useRef, useEffect } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { toast } from 'sonner';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * GoogleSignInButton — custom-styled shell that DELEGATES the actual click
 * to Google's official <GoogleLogin> iframe rendered invisibly on top.
 *
 * This preserves the mandatory Google identity flow (JWT credential in a
 * secure iframe) while letting us match the app's h-12 rounded-xl look.
 *
 * Props:
 *   - mode: "auth" | "link"
 *   - onSuccess: (data) => void
 *   - onError:   (err)  => void
 *   - text: "signin_with" | "signup_with" | "continue_with"
 *   - locale: "ar" | "en"
 */
export default function GoogleSignInButton({
  mode = 'auth',
  onSuccess,
  onError,
  text = 'signin_with',
  locale = 'ar',
}) {
  const [loading, setLoading] = useState(false);
  const [containerWidth, setContainerWidth] = useState(360);
  const wrapperRef = useRef(null);
  const isAr = locale === 'ar';

  // Keep Google iframe width in sync with our wrapper so clicks always hit it.
  useEffect(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;
    const update = () => {
      // Google's SDK caps the width at 400px.
      const w = Math.min(Math.max(el.offsetWidth || 320, 200), 400);
      setContainerWidth(w);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // In "link" mode, tell Google to forget any previously auto-selected
  // account so clicking the button always opens the chooser. Without this,
  // Google's SDK can silently no-op the second click after a link/unlink
  // cycle within the same page load.
  useEffect(() => {
    if (mode !== 'link') return;
    try { window.google?.accounts?.id?.disableAutoSelect?.(); } catch (_) {}
  }, [mode]);

  const handleCredential = async (response) => {
    const credential = response?.credential;
    if (!credential) {
      toast.error(isAr ? 'لم يصل رمز Google' : 'No Google credential received');
      return;
    }

    setLoading(true);
    try {
      const url = mode === 'link'
        ? `${BACKEND_URL}/api/auth/google/direct/link`
        : `${BACKEND_URL}/api/auth/google/direct`;

      const headers = { 'Content-Type': 'application/json' };
      if (mode === 'link') {
        const token = localStorage.getItem('token');
        if (!token) {
          toast.error(isAr ? 'يجب تسجيل الدخول أولاً' : 'Please login first');
          setLoading(false);
          return;
        }
        headers.Authorization = `Bearer ${token}`;
      }

      const res = await axios.post(url, { credential }, { headers });

      if (mode === 'auth') {
        if (res.data?.access_token) {
          localStorage.setItem('token', res.data.access_token);
        }
        if (res.data?.user) {
          localStorage.setItem('user', JSON.stringify(res.data.user));
        }
      }

      onSuccess?.(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg;
      if (detail && typeof detail === 'object') {
        msg = isAr ? detail.message_ar : detail.message_en;
      } else {
        msg = detail || (isAr ? 'فشل تسجيل الدخول بحساب Google' : 'Google sign-in failed');
      }
      toast.error(msg);
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  const label = loading
    ? (isAr ? 'جارِ الاتصال…' : 'Connecting…')
    : mode === 'link'
      ? (isAr ? 'ربط حساب Google' : 'Link Google account')
      : text === 'signup_with'
        ? (isAr ? 'التسجيل باستخدام Google' : 'Sign up with Google')
        : (isAr ? 'المتابعة باستخدام Google' : 'Continue with Google');

  return (
    <div
      ref={wrapperRef}
      className="relative w-full h-12"
      data-testid={`google-signin-btn-${mode}`}
      style={{ opacity: loading ? 0.6 : 1 }}
    >
      {/* Visible custom button — designed to match Apple + submit button */}
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        className="w-full h-12 inline-flex items-center justify-center gap-2.5 rounded-xl bg-white text-[#3c4043] text-[15px] font-medium leading-none border border-[#dadce0] shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:bg-[#f8f9fa] hover:border-[#c6c9ce] active:scale-[0.99] transition-all duration-150 pointer-events-none"
      >
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        <span>{label}</span>
      </button>

      {/* Invisible Google official button placed exactly on top — receives the click.
          Sized to fill our shell so any tap on the shell hits the Google iframe. */}
      <div
        className="absolute inset-0 overflow-hidden rounded-xl"
        style={{ colorScheme: 'light', opacity: 0.01, pointerEvents: 'auto', zIndex: 2 }}
      >
        <GoogleLogin
          onSuccess={handleCredential}
          onError={() => {
            toast.error(isAr ? 'فشل الاتصال بـ Google' : 'Google connection failed');
            onError?.(new Error('google-oauth-error'));
          }}
          text={text}
          locale={isAr ? 'ar_SA' : 'en_US'}
          theme="filled_blue"
          shape="rectangular"
          size="large"
          width={containerWidth}
        />
      </div>
    </div>
  );
}
